// KalodataUpload — admin UI to import a Kalodata creator export (.xlsx/.csv) into research_creators,
// replacing the kalodata-creator-import CLI. Parses the sheet IN THE BROWSER (SheetJS from CDN), then
// POSTs the raw rows to the funnel's /shop/creators/seed (the funnel runs the existing adapter). The
// funnel is admin-reachable with the same x-research-token the crawler uses.
//
// Why client-side parse + direct funnel POST: keeps the funnel service lean (no xlsx dep there) and
// avoids a new backend→funnel proxy env. Admin enters the funnel URL+token once (persisted locally).

import { useEffect, useRef, useState } from "react";
import { Modal } from "@/components/ui/Modal.tsx";
import { Card, CardContent } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Input } from "@/components/ui/Input.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { useToast } from "@/stores/toastStore.ts";

// SheetJS from CDN — loaded once, on demand (no package.json change).
const XLSX_CDN = "https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js";
function loadXlsx(): Promise<any> {
  return new Promise((resolve, reject) => {
    if ((window as any).XLSX) return resolve((window as any).XLSX);
    const s = document.createElement("script");
    s.src = XLSX_CDN;
    s.onload = () => resolve((window as any).XLSX);
    s.onerror = () => reject(new Error("could not load the spreadsheet parser (CDN blocked?)"));
    document.head.appendChild(s);
  });
}

interface KalodataUploadProps { onClose: () => void; }

export function KalodataUpload({ onClose }: KalodataUploadProps) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [funnelUrl, setFunnelUrl] = useState("");
  const [funnelToken, setFunnelToken] = useState("");
  const [niche, setNiche] = useState("kalodata-top");
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [result, setResult] = useState<{ seeded: number; updated: number; total: number; withRevenue: number } | null>(null);

  // remember funnel url/token locally (same values the crawler uses)
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("kalodata-import") || "{}");
      if (saved.funnelUrl) setFunnelUrl(saved.funnelUrl);
      if (saved.funnelToken) setFunnelToken(saved.funnelToken);
      if (saved.niche) setNiche(saved.niche);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    try { localStorage.setItem("kalodata-import", JSON.stringify({ funnelUrl, funnelToken, niche })); } catch { /* ignore */ }
  }, [funnelUrl, funnelToken, niche]);

  async function onFile(file: File) {
    setParsing(true); setResult(null); setRows([]); setFileName(file.name);
    try {
      const XLSX = await loadXlsx();
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      // defval:"" so every cell is a string; the funnel adapter maps by header name.
      const parsed = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false }) as Record<string, string>[];
      setRows(parsed);
      if (!parsed.length) toast.error("No rows found in the first sheet.");
      else if (!("Handle" in parsed[0])) toast.error('No "Handle" column — is this a Kalodata CREATOR export?');
    } catch (e) {
      toast.error(`Parse failed: ${(e as Error).message}`);
    } finally { setParsing(false); }
  }

  async function seed() {
    if (!funnelUrl.trim() || !funnelToken.trim()) { toast.error("Funnel URL + token required."); return; }
    if (!rows.length) { toast.error("Upload a file first."); return; }
    setSeeding(true); setResult(null);
    try {
      // Chunk to stay under the funnel's 2MB request-body cap (~5000 rows ≈ 2.2MB). Per-row upsert is
      // idempotent, so batching is safe. Aggregate the counts across batches.
      const CHUNK = 1000;
      const base = funnelUrl.trim().replace(/\/$/, "");
      const nicheTag = niche.trim() || "kalodata-top";
      const agg = { seeded: 0, updated: 0, total: 0, withRevenue: 0 };
      for (let i = 0; i < rows.length; i += CHUNK) {
        const batch = rows.slice(i, i + CHUNK);
        const res = await fetch(`${base}/shop/creators/seed`, {
          method: "POST",
          headers: { "content-type": "application/json", "x-research-token": funnelToken.trim() },
          body: JSON.stringify({ rows: batch, niche: nicheTag }),
        });
        const text = await res.text();
        if (!res.ok) { toast.error(`Batch ${i / CHUNK + 1} failed (${res.status}): ${text.slice(0, 140)}`); return; }
        const b = JSON.parse(text);
        agg.seeded += b.seeded ?? 0; agg.updated += b.updated ?? 0;
        agg.total += b.total ?? 0; agg.withRevenue += b.withRevenue ?? 0;
      }
      setResult(agg);
      toast.success(`Imported ${agg.total} creators (${agg.seeded} new, ${agg.updated} updated).`);
    } catch (e) {
      toast.error(`Seed request failed: ${(e as Error).message}`);
    } finally { setSeeding(false); }
  }

  const headers = rows.length ? Object.keys(rows[0]) : [];
  const preview = rows.slice(0, 5);

  return (
    <Modal open onClose={onClose} size="lg"
      title="Import Kalodata creators"
      description="Upload a Kalodata creator export (.xlsx/.csv). It seeds research_creators with the revenue signal that gates creator vision.">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Funnel URL" placeholder="http://74.208.126.241:9401"
            value={funnelUrl} onChange={(e) => setFunnelUrl(e.target.value)} />
          <Input label="Funnel token (x-research-token)" type="password"
            value={funnelToken} onChange={(e) => setFunnelToken(e.target.value)} />
        </div>
        <Input label="Niche tag for this batch" value={niche} onChange={(e) => setNiche(e.target.value)} />

        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
        <Button variant="outline" onClick={() => fileRef.current?.click()} loading={parsing}>
          {fileName ? `Change file (${fileName})` : "Choose .xlsx / .csv"}
        </Button>

        {parsing && <div className="flex justify-center py-6"><Spinner /></div>}

        {rows.length > 0 && !parsing && (
          <Card><CardContent className="space-y-2 py-3">
            <div className="flex items-center gap-2 text-[13px]">
              <Badge variant="success">{rows.length} rows</Badge>
              {"Handle" in (rows[0] ?? {}) ? <Badge variant="success">Handle ✓</Badge> : <Badge variant="danger">no Handle col</Badge>}
              {"Revenue($)" in (rows[0] ?? {}) ? <Badge variant="success">Revenue ✓</Badge> : <Badge variant="warning">no Revenue col</Badge>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead><tr className="text-surface-500">
                  {headers.slice(0, 6).map((h) => <th key={h} className="px-2 py-1 text-left font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {preview.map((r, i) => (
                    <tr key={i} className="border-t border-surface-100">
                      {headers.slice(0, 6).map((h) => <td key={h} className="truncate px-2 py-1">{r[h]}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-surface-400">Preview of first 5 rows / 6 columns. The funnel maps by header name.</p>
          </CardContent></Card>
        )}

        {result && (
          <Card><CardContent className="py-3 text-[13px] text-emerald-700">
            Imported {result.total} creators — {result.seeded} new, {result.updated} updated,
            {" "}{result.withRevenue} with a revenue signal (the creator-vision gate).
          </CardContent></Card>
        )}
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button disabled={!rows.length || seeding} loading={seeding} onClick={seed}>
          Import {rows.length || ""} creators
        </Button>
      </div>
    </Modal>
  );
}
